import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert, Container, CircularProgress, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resendVerificationApi, meApi } from '../api/auth';

const AccountStatusGate = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [isVerified, setIsVerified] = useState(Boolean(user?.emailVerified));
  const [approvalStatus, setApprovalStatus] = useState(user?.approvalStatus || 'pending');

  useEffect(() => {
    // Fetch fresh user status to reflect latest verification/approval
    const run = async () => {
      const latest = await refreshUser();
      if (latest) {
        setIsVerified(Boolean(latest.emailVerified));
        setApprovalStatus(latest.approvalStatus || 'pending');
      }
    };
    run();
  }, []);

  const resend = async () => {
    setStatus('loading');
    setMessage('');
    try {
      const data = await resendVerificationApi({ email: user?.email });
      if (data?.status === 'success') {
        setStatus('success');
        setMessage('Verification email sent. Please check your inbox.');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Failed to send verification email');
      }
    } catch (e) {
      setStatus('error');
      setMessage(e?.response?.data?.message || 'Failed to send verification email');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white' }}>
        <Container maxWidth="sm">
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: { xs: 3, sm: 5 } }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Account setup required</Typography>

            {!isVerified ? (
              <>
                <Typography sx={{ mb: 2 }}>Your email is not verified. Please verify your email to continue.</Typography>
                {message && (
                  <Alert severity={status === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>{message}</Alert>
                )}
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={resend} disabled={status === 'loading'} sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#009bb3' } }}>
                    {status === 'loading' ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Resend verification link'}
                  </Button>
                  <Button variant="outlined" color="inherit" onClick={handleLogout}>Logout</Button>
                </Stack>
              </>
            ) : approvalStatus !== 'approved' ? (
              <>
                <Typography sx={{ mb: 2 }}>Your account is awaiting superadmin approval. Please contact your administrator.</Typography>
                <Stack direction="row" spacing={2}>
                  <Button variant="outlined" onClick={handleLogout}>Logout</Button>
                  <Button variant="text" onClick={async ()=>{ setStatus('loading'); await refreshUser(); setStatus('idle'); }}>Refresh</Button>
                </Stack>
              </>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>Your account is ready. You can access the dashboard.</Alert>
                <Button variant="contained" onClick={() => navigate('/')} sx={{ bgcolor: '#00bcd4', '&:hover': { bgcolor: '#009bb3' } }}>Go to Dashboard</Button>
              </>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AccountStatusGate;


